import _ from "lodash";
import { getIsWalkMeIntegrationEnabled } from "Utils/ACLStoreUtils";
import { walkMeSDKScriptUrl } from "config";
import {
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import { store } from "store/createStore";
import {
  getAcademicYearId,
  getSelectedCourseId,
  getSelectedCurriculumId,
  getSelectedCurriculumLabel,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { isTouchableV3 } from "Utils";

const WALK_ME_CURRICULUM_SCHOOL_TYPE_MAPPING = {
  [CURRICULUM_TYPE_MYP]: "secondary",
  [CURRICULUM_TYPE_UBD]: "secondary",
  [CURRICULUM_TYPE_DP]: "secondary",
  [CURRICULUM_TYPE_PYP]: "primary",
};

const WALKME_PAGE_NAMES = {
  HOME_PAGE: "homePage",
  CLASS_SETTINGS: "classSettings",
  UNIT_PLANNING: "unitPlanning",
  WEEKLY_PLANNER: "weeklyPlanner",
  ASSIGNMENTS: "assignments",
  WEEKLY_PLANNER_BLOCKS: "weeklyPlannerBlocks",
  PLANNER_BLOCK_CONTENT: "plannerBlockContent",
  ASSIGN_FROM_UNIT: "assignFromUnit",
  CLASS_FLOW: "classFlow",
  IMPORT_FROM_UNIT: "importFromUnit",
  PORTFOLIO: "portfolio",
  NEW_POST: "newPost",
  CREATE_LEARNING_EXPERIENCE: "createLearningExperience",
};

const COURSES_BASE_ROUTE = "courses"; // Home page
const HOME_PAGE_ROUTES_WITH_EXPLICIT_WALKME_PAGE_NAMES = [
  "class-settings",
  "unitPlans",
  "classroom",
  "class-planners",
  "weekly",
  "class-flow",
  "journal",
];

class WalkMeManager {
  static isLoaded = false;
  static isLoading = false;
  static loadPromise = null;
  static unsubscribeStore = null;
  static currentWalkMeVariables = {};
  static currentGrades = []; // Store grades separately since they come from GraphQL
  static currentPageDetails = {
    pageName: null, // Current page name for WalkMe
    routeId: null, // Id of the route which set the page name
  };

  /**
   * Get WalkMe variables from Redux state (excluding grades)
   * @returns {Object} - WalkMe variables object without grades
   */
  static getWalkMeVariablesFromRedux() {
    const state = store.getState();

    const portalType = "PLANNER";
    const userInfo = getUserInfo({ portalType });
    const courseId = getSelectedCourseId(state);

    let courseTitle = null;
    if (!_.isEmpty(courseId)) {
      const courseDetails = getCourseDetailsFromCache({
        id: courseId,
        courseGradeFilters: {
          academicYearId: getAcademicYearId(state),
        },
      });
      courseTitle = _.get(courseDetails, "title", "");
    }

    return {
      userId: userInfo.id,
      userType: userInfo.user_type,
      courseId,
      courseTitle,
      currentCurriculumProgramLabel: getSelectedCurriculumLabel(state),
      currentCurriculumProgramType:
        getCurrentCurriculumProgramType(state) || CURRICULUM_TYPE_PYP,
      currentCurriculumProgramId: getSelectedCurriculumId(state),
    };
  }

  /**
   * Get complete WalkMe variables including grades
   * @returns {Object} - Complete WalkMe variables object
   */
  static getCompleteWalkMeVariables() {
    const reduxVariables = this.getWalkMeVariablesFromRedux();
    return {
      ...reduxVariables,
      grades: this.currentGrades,
      pageName: this.currentPageDetails.pageName, // Include current page
    };
  }

  /**
   * Initialize WalkMe script with user variables
   * @param {Object} params - Optional parameters
   * @param {Array} params.grades - Organization grades
   * @returns {Promise} - Promise that resolves when script is loaded
   */
  static async initialize({ grades = [] }) {
    // If WalkMe integration not enabled, do nothing
    if (!getIsWalkMeIntegrationEnabled()) {
      return Promise.resolve();
    }

    // Update grades if provided
    if (grades.length > 0) {
      this.currentGrades = grades;
    }

    // Set up Redux store subscription to watch for changes
    this.setupStoreSubscription();
    const completeVariables = this.getCompleteWalkMeVariables();

    // Check if already initialized
    if (this.isLoaded || this.isScriptPresent()) {
      // Update variables immediately if already loaded
      this.updateVariables(completeVariables);
      return Promise.resolve();
    }

    // If loading is in progress, return existing promise
    if (this.isLoading && this.loadPromise) {
      try {
        await this.loadPromise;
        this.updateVariables(completeVariables);
        return;
      } catch (error) {
        return;
      }
    }

    // Otherwise, load the script
    try {
      await this.loadScript();
    } catch (error) {
      return;
    }
  }

  /**
   * Update grades and refresh WalkMe variables
   * @param {Array} grades - New grades array from GraphQL query
   */
  static updateGrades(grades = []) {
    const previousGrades = this.currentGrades;
    this.currentGrades = grades;

    // Only update WalkMe if grades actually changed and script is loaded
    const gradesChanged = !_.isEqual(previousGrades, grades);
    if (gradesChanged && (this.isLoaded || this.isScriptPresent())) {
      const completeVariables = this.getCompleteWalkMeVariables();
      this.updateVariables(completeVariables);
      this.currentWalkMeVariables = { ...completeVariables };
    }
  }

  /**
   * Set the current page name for WalkMe
   *
   * Prevents race conditions during rapid route transitions:
   * If a page tries to clear the page name (sets it to null),
   * it is allowed only if it was the one that originally set it.
   *
   * @param {Object} params - Parameters for setting page name
   * @param {string} params.pageName - Name of the current page
   * @param {string} params.routeId - Id of the route which set the page name
   */
  static setCurrentPageName({ pageName = null, routeId = null }) {
    const currentPageDetails = this.currentPageDetails;
    const previousPageName = _.get(currentPageDetails, "pageName");

    // If setting page name to null, only allow it if it's from the same route that set the current page name
    // This prevents race conditions where an old route clears the name after a new one sets it
    if (_.isNull(pageName)) {
      const currentRouteId = _.get(currentPageDetails, "routeId");
      if (
        !_.isNull(currentRouteId) &&
        !_.isNull(routeId) &&
        currentRouteId !== routeId
      ) {
        // Don't reset if a different route is trying to clear the page name
        return;
      }
    }

    // Update current page name and routeId which set the page name
    this.currentPageDetails = {
      pageName,
      routeId,
    };

    // Only update WalkMe if page name actually changed and script is loaded
    const pageNameChanged = !_.isEqual(previousPageName, pageName);
    if (pageNameChanged && (this.isLoaded || this.isScriptPresent())) {
      const completeVariables = this.getCompleteWalkMeVariables();
      this.updateVariables(completeVariables);
      this.currentWalkMeVariables = { ...completeVariables };
    }
  }

  /**
   * Resets WalkMe page name when navigating to certain course routes
   * that do not have explicit WalkMe page mappings.
   *
   * Some subroutes under the 'courses' base route fall back to the default
   * home page name due to missing explicit WalkMe page names. To prevent this,
   * we reset the current page name when such routes are detected.
   *
   * @param {*} pathname - Current route pathname
   */
  static resetWalkMePageNameForUnmappedCourseRoutes(pathname) {
    // If WalkMe integration not enabled, do nothing
    if (!getIsWalkMeIntegrationEnabled()) {
      return;
    }

    // If pathname is not provided or does not include the courses base route, do nothing
    if (!pathname || !_.includes(pathname, COURSES_BASE_ROUTE)) {
      return;
    }

    if (_.endsWith(pathname, COURSES_BASE_ROUTE)) {
      // If the pathname is exactly the courses base route, reset the page name
      this.setCurrentPageName({
        pageName: WALKME_PAGE_NAMES.HOME_PAGE,
        routeId: WALKME_PAGE_NAMES.HOME_PAGE,
      });
      return;
    }

    // Check if the path is not one of the home page routes with explicit WalkMe page names
    const isPathWithoutExplicitWalkMePageName = !_.some(
      HOME_PAGE_ROUTES_WITH_EXPLICIT_WALKME_PAGE_NAMES,
      pageRoute => _.includes(pathname, pageRoute)
    );

    // If the path does not have an explicit WalkMe page name, reset the current page name
    if (isPathWithoutExplicitWalkMePageName) {
      this.setCurrentPageName({
        pageName: null,
        routeId: null,
      });
    }
  }

  /**
   * Set up Redux store subscription to watch for relevant changes
   */
  static setupStoreSubscription() {
    if (this.unsubscribeStore) {
      return; // Already subscribed
    }

    this.unsubscribeStore = store.subscribe(() => {
      const newReduxVariables = this.getWalkMeVariablesFromRedux();
      const currentReduxVariables = _.omit(this.currentWalkMeVariables, [
        "grades",
        "pageName",
      ]);

      // Check if relevant data has changed
      const hasChanged = !_.isEqual(currentReduxVariables, newReduxVariables);

      if (hasChanged && (this.isLoaded || this.isScriptPresent())) {
        const completeVariables = this.getCompleteWalkMeVariables();
        this.updateVariables(completeVariables);
        this.currentWalkMeVariables = { ...completeVariables };
      }
    });

    // Initialize current state
    this.currentWalkMeVariables = this.getWalkMeVariablesFromRedux();
  }

  /**
   * Check if WalkMe script is already present
   * @returns {boolean} - True if script exists
   */
  static isScriptPresent() {
    const hasDataAttribute = !!document.querySelector(
      'script[data-walkme="true"]'
    );
    const hasWindowConfig = !!window._walkmeConfig;

    const isPresent = hasDataAttribute || hasWindowConfig;
    if (isPresent && !this.isLoaded) {
      this.isLoaded = true;
    }

    return isPresent;
  }

  /**
   * Load WalkMe script
   * @returns {Promise} - Promise that resolves when script loads
   */
  static async loadScript() {
    // If already loaded or script is present, resolve immediately
    if (this.isLoaded || this.isScriptPresent()) {
      return Promise.resolve();
    }

    // Double check before creating promise
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    // Set loading state immediately
    this.isLoading = true;

    this.loadPromise = new Promise((resolve, reject) => {
      // If already loaded, resolve immediately
      if (this.isScriptPresent()) {
        this.isLoading = false;
        this.loadPromise = null;
        resolve();
        return;
      }

      try {
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.setAttribute("data-walkme", "true");

        // Set up proper event handlers
        script.onload = () => {
          this.isLoaded = true;
          this.isLoading = false;
          this.loadPromise = null;

          resolve();
        };

        script.onerror = () => {
          this.isLoading = false;
          this.loadPromise = null;

          const error = new Error("Failed to load WalkMe script");
          reject(error);
        };

        script.innerHTML = `
          (function() {
            var walkme = document.createElement('script');
            walkme.type = 'text/javascript'; 
            walkme.async = true;
            walkme.src = '${walkMeSDKScriptUrl}';
            var s = document.getElementsByTagName('script')[0];
            s.parentNode.insertBefore(walkme, s); 
            window._walkmeConfig = { smartLoad: true }; 
          })();
        `;

        // Add to head
        document.head.appendChild(script);

        // set walkme_ready callback
        window.walkme_ready = () => {
          this.onWalkMeReady();
        };
      } catch (error) {
        this.isLoading = false;
        this.loadPromise = null;

        reject(error);
      }
    });

    return this.loadPromise;
  }

  /**
   * Update WalkMe window variables with user and organization data
   * @param {Object} params - User and organization data
   * @param {Array} params.grades - Organization grades
   * @param {string} params.userId - Current user ID
   * @param {string} params.userType - Current user type
   * @param {string} params.pageName - Current page name (optional)
   * @param {string} params.courseId - Current course ID
   * @param {string} params.courseTitle - Current course title
   * @param {string} params.currentCurriculumProgramId - Current curriculum program ID
   * @param {string} params.currentCurriculumProgramType - Current curriculum type
   * @param {string} params.currentCurriculumProgramLabel - Current curriculum program label
   */
  static updateVariables({
    grades = [],
    userId,
    userType,
    pageName = null,
    courseId,
    courseTitle,
    currentCurriculumProgramId,
    currentCurriculumProgramType,
    currentCurriculumProgramLabel,
  }) {
    let schoolType =
      WALK_ME_CURRICULUM_SCHOOL_TYPE_MAPPING[currentCurriculumProgramType];

    if (currentCurriculumProgramType === CURRICULUM_TYPE_PYP) {
      const isKindergarten = _.some(grades, grade =>
        _.includes(["K1", "K2"], grade.name)
      );
      if (isKindergarten) {
        schoolType = "kindergarten";
      }
    }

    window.walkMeSchoolType = schoolType;

    if (currentCurriculumProgramLabel) {
      window.walkMeSchoolName = currentCurriculumProgramLabel;
    }

    if (userId) {
      window.walkMeUserId = userId;
    }

    if (userType) {
      window.walkMeUserType = userType;
    }

    if (currentCurriculumProgramId) {
      try {
        const parsedUrl = new URL(window.location.href);
        window.walkMeUrl = `${parsedUrl.origin}/platform/${currentCurriculumProgramId}/courses`;
      } catch (e) {
        window.walkMeUrl = null;
      }
    }

    window.walkMePageName = pageName;
    window.walkMeCourseId = !_.isEmpty(courseId) ? courseId : null;
    window.walkMeCourseTitle = !_.isEmpty(courseTitle) ? courseTitle : null;
  }

  /**
   * Check and update variables if walkMeUserId is not set
   * This handles cases where the script loaded but variables weren't set properly
   */
  static checkAndUpdateVariables() {
    if (getIsWalkMeIntegrationEnabled() && !window.walkMeUserId) {
      this.updateVariables(this.getCompleteWalkMeVariables());
    }
  }

  /**
   * WalkMe ready callback
   * Called when WalkMe script has fully loaded
   * Sets initial variables and hides walkme button on mobile
   */
  static onWalkMeReady() {
    if (getIsWalkMeIntegrationEnabled()) {
      this.updateVariables(this.getCompleteWalkMeVariables());

      // Ensure WalkMe button is hidden on mobile
      if (window.WalkMePlayerAPI && isTouchableV3()) {
        window.WalkMePlayerAPI.removePlayer();
      }
    }
  }

  /**
   * Clean up WalkMe scripts and variables - only call when needed
   * Use cases: user logout, org switch
   */
  static cleanup() {
    // Unsubscribe from Redux store
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }

    const walkMeProps = [
      "walkMeSchoolType",
      "walkMeUserId",
      "walkMeUserType",
      "walkMeSchoolName",
      "walkMePageName",
      "walkMeUrl",
      "walkMeCourseId",
      "walkMeCourseTitle",
      "_walkmeConfig",
    ];

    // Remove WalkMe related window properties
    _.forEach(walkMeProps, prop => {
      if (window[prop]) {
        delete window[prop];
      }
    });

    if (window._walkMe) {
      window._walkMe.removeWalkMe(); // Remove WalkMe instance
    }

    // Remove all WalkMe related scripts
    const walkMeScripts = [
      'script[data-walkme="true"]',
      'script[src*="walkme"]',
    ];

    // Remove scripts matching selectors
    _.forEach(walkMeScripts, selector => {
      _.forEach(document.querySelectorAll(selector), script => {
        script.remove();
      });
    });

    // Reset state
    this.isLoaded = false;
    this.isLoading = false;
    this.loadPromise = null;
    this.currentWalkMeVariables = {};
    this.currentGrades = [];
    this.currentPageDetails = {
      pageName: null,
      routeId: null,
    };
  }
}

export default WalkMeManager;
export { WALKME_PAGE_NAMES };
